import { useMemo, forwardRef } from 'react'
import * as jsondiffpatch from 'jsondiffpatch'
import { LoadingButton } from '@mui/lab'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid2,
  Typography,
  Box,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  RichTreeViewPro,
  UseTreeItemParameters,
  useTreeItem,
  useTreeItemModel,
  TreeItemProvider,
  TreeItemGroupTransition,
  TreeItemLabel,
} from '@mui/x-tree-view-pro'

interface DiffModalProps {
  open: boolean
  onClose: () => void
  onResync: () => void
  originalData: Record<string, unknown>
  newData: Record<string, unknown>
  isResyncingFile: boolean
}

interface TreeNode {
  id: string
  label: string
  value?: unknown
  children?: TreeNode[]
  isDifferent?: boolean
  isAdded?: boolean
  isRemoved?: boolean
}

// Styled components
const TreeItemRoot = styled('li')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  outline: 0,
  color: theme.palette.text.primary,
}))

const TreeItemContent = styled('div')<{ isDifferent?: boolean }>(({ theme, isDifferent }) => ({
  padding: theme.spacing(0.5),
  paddingRight: theme.spacing(1),
  paddingLeft: `calc(${theme.spacing(
    1,
  )} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))`,
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  borderRadius: theme.spacing(0.5),
  marginBottom: theme.spacing(0.25),
  marginTop: theme.spacing(0.25),
  fontWeight: 500,
  ...(isDifferent && {
    backgroundColor: 'rgba(255, 193, 7, 0.15)',
    border: '1px solid rgba(255, 193, 7, 0.4)',
    '&:hover': {
      backgroundColor: 'rgba(255, 193, 7, 0.25)',
    },
  }),
  '&:hover': {
    backgroundColor: isDifferent ? 'rgba(255, 193, 7, 0.25)' : theme.palette.action.hover,
  },
}))

// Custom TreeItem component for diff highlighting
type Props = Omit<UseTreeItemParameters, 'rootRef'>

const CustomTreeItem = forwardRef<HTMLLIElement, Props>(function CustomTreeItem(props, ref) {
  const { id, itemId, label, disabled, children, ...other } = props

  const {
    getContextProviderProps,
    getRootProps,
    getContentProps,
    getLabelProps,
    getGroupTransitionProps,
  } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref })

  const item = useTreeItemModel<TreeNode>(itemId)!

  return (
    <TreeItemProvider {...getContextProviderProps()}>
      <TreeItemRoot {...getRootProps(other)}>
        <TreeItemContent {...getContentProps()} isDifferent={item.isDifferent}>
          {/* Removed TreeItemIconContainer and TreeItemIcon to hide expand icons */}

          <TreeItemLabel {...getLabelProps()}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{label}</Typography>
            </Box>
          </TreeItemLabel>
        </TreeItemContent>
        {children && <TreeItemGroupTransition {...getGroupTransitionProps()} />}
      </TreeItemRoot>
    </TreeItemProvider>
  )
})

const DiffModal = ({
  open,
  onClose,
  onResync,
  originalData,
  newData,
  isResyncingFile,
}: DiffModalProps) => {
  // Calculate differences using jsondiffpatch
  const diff = useMemo(() => {
    return jsondiffpatch.diff(originalData, newData)
  }, [originalData, newData])

  // Helper function to check if a specific path has differences
  const hasDifferenceAtPath = (path: string): boolean => {
    if (!diff) return false

    const pathParts = path.split('.')
    let currentDiff: unknown = diff

    for (const part of pathParts) {
      if (currentDiff && typeof currentDiff === 'object' && part in currentDiff) {
        currentDiff = (currentDiff as Record<string, unknown>)[part]
      } else {
        return false
      }
    }

    return currentDiff !== undefined
  }

  // Convert JSON object to tree structure
  const jsonToTree = (obj: Record<string, unknown>, prefix = '', isOriginal = true): TreeNode[] => {
    const nodes: TreeNode[] = []

    if (obj === null || obj === undefined) {
      return [{ id: prefix, label: `${prefix}: ${obj}`, value: obj }]
    }

    if (typeof obj !== 'object' || Array.isArray(obj)) {
      return [{ id: prefix, label: `${prefix}: ${JSON.stringify(obj)}`, value: obj }]
    }

    Object.entries(obj).forEach(([key, value]) => {
      const nodeId = prefix ? `${prefix}.${key}` : key
      const isDifferent = hasDifferenceAtPath(nodeId)

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        nodes.push({
          id: nodeId,
          label: key,
          children: jsonToTree(value as Record<string, unknown>, nodeId, isOriginal),
          isDifferent,
        })
      } else {
        nodes.push({
          id: nodeId,
          label: `${key}: ${JSON.stringify(value)}`,
          value,
          isDifferent,
        })
      }
    })

    return nodes
  }

  const originalTree = jsonToTree(originalData, '', true)
  const newTree = jsonToTree(newData, '', false)

  // Convert TreeNode to TreeViewBaseItem format with isDifferent property
  const convertToTreeViewItems = (nodes: TreeNode[]): TreeNode[] => {
    return nodes.map(node => ({
      id: node.id,
      label: node.label,
      children: node.children ? convertToTreeViewItems(node.children) : undefined,
      isDifferent: node.isDifferent,
      value: node.value,
      isAdded: node.isAdded,
      isRemoved: node.isRemoved,
      // Force items to not be expandable to hide expand icons
      expandable: false,
    }))
  }

  const originalTreeViewItems = convertToTreeViewItems(originalTree)
  const newTreeViewItems = convertToTreeViewItems(newTree)

  // Get all item IDs to expand everything
  const getAllItemIds = (nodes: TreeNode[]): string[] => {
    const ids: string[] = []
    const traverse = (nodeList: TreeNode[]) => {
      nodeList.forEach(node => {
        ids.push(node.id)
        if (node.children) {
          traverse(node.children)
        }
      })
    }
    traverse(nodes)
    return ids
  }

  const allOriginalIds = getAllItemIds(originalTree)
  const allNewIds = getAllItemIds(newTree)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Elementdaten Vergleich</DialogTitle>
      <DialogContent>
        <Grid2 container spacing={2} sx={{ mt: 1 }}>
          <Grid2 size={6}>
            <Typography variant="h6" gutterBottom>
              Vorherige Daten
            </Typography>
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 1,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              <RichTreeViewPro
                items={originalTreeViewItems}
                defaultExpandedItems={allOriginalIds}
                expandedItems={allOriginalIds} // Force items to stay expanded
                onExpandedItemsChange={() => {
                  // TODO
                }} // Prevent expansion changes
                slots={{ item: CustomTreeItem }}
                sx={{
                  height: 'auto',
                  flexGrow: 1,
                  maxWidth: '100%',
                  overflowY: 'auto',
                  // Hide expand icons
                  '& .MuiTreeItem-iconContainer': {
                    display: 'none',
                  },
                  '& .MuiTreeItem-expandIcon': {
                    display: 'none',
                  },
                }}
              />
            </Box>
          </Grid2>
          <Grid2 size={6}>
            <Typography variant="h6" gutterBottom>
              Aktuelle Daten
            </Typography>
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 1,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              <RichTreeViewPro
                items={newTreeViewItems}
                defaultExpandedItems={allNewIds}
                expandedItems={allNewIds} // Force items to stay expanded
                onExpandedItemsChange={() => {
                  // TODO
                }} // Prevent expansion changes
                slots={{ item: CustomTreeItem }}
                sx={{
                  height: 'auto',
                  flexGrow: 1,
                  maxWidth: '100%',
                  overflowY: 'auto',
                  // Hide expand icons
                  '& .MuiTreeItem-iconContainer': {
                    display: 'none',
                  },
                  '& .MuiTreeItem-expandIcon': {
                    display: 'none',
                  },
                }}
              />
            </Box>
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Schließen
        </Button>
        <LoadingButton
          onClick={onResync}
          variant="contained"
          color="primary"
          loading={isResyncingFile}
        >
          Neu synchronisieren
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default DiffModal
